import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type BankReconcileCommitRow,
  type BankReconcilePreviewResponse,
  type BankReconcileRow,
  type BankReconcileStatus,
} from '@utazzo/shared';
import { UzAlert, UzBadge, UzButton, UzCard, UzCheckbox, UzDialog, UzSpinner } from '@utazzo/ui';
import { History, Upload } from 'lucide-react';
import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import {
  commitBankReconcile,
  getBankReconcileHistory,
  getBankReconcileRunDetail,
  previewBankReconcile,
} from '../invoices.api';
import { formatDateDMY, formatInrPaise } from '../invoices.helpers';

import { PageShell } from '@/components/page-shell';
import { formatLocalDMY } from '@/lib/date-format';

const HISTORY_KEY = ['reports', 'bank-reconcile', 'history'] as const;

/** A run timestamp as local "DD-MM-YYYY HH:MM". */
const formatRunTime = (iso: string): string => {
  const date = formatLocalDMY(iso);
  const time = new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return date ? `${date} ${time}` : '';
};

/**
 * Bank Book reconciliation (route /reports/bank-reconcile). Upload the client's
 * "Working Sheet" bank ledger; we match every invoice-linked Cr / Dr row against
 * the recorded customer receipts and supplier payments, then let finance create
 * the missing entries and update the mismatched ones (skipping any row on
 * purpose). Non-invoice rows (contra / expense / loan) are reported as N/A.
 */

const STATUS_META: Record<
  BankReconcileStatus,
  { label: string; tone: 'default' | 'secondary' | 'success' | 'warning' | 'destructive' }
> = {
  MATCHED: { label: 'Matched', tone: 'success' },
  MISMATCH: { label: 'Mismatch', tone: 'warning' },
  MISSING: { label: 'Missing', tone: 'default' },
  INVOICE_NOT_FOUND: { label: 'Invoice not found', tone: 'destructive' },
  INVOICE_AMBIGUOUS: { label: 'Ambiguous', tone: 'destructive' },
  NON_INVOICE: { label: 'N/A (not an invoice)', tone: 'secondary' },
};

type Filter = 'ALL' | BankReconcileStatus;

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // FileReader returns "data:<mime>;base64,<payload>" - strip the prefix.
      const result = String(reader.result);
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(new Error('Could not read the file'));
    reader.readAsDataURL(file);
  });

export const BankReconcilePage = (): React.JSX.Element => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<BankReconcilePreviewResponse | null>(null);
  const [skipped, setSkipped] = useState<Set<number>>(new Set());
  const [filter, setFilter] = useState<Filter>('ALL');
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [detailRunId, setDetailRunId] = useState<string | null>(null);
  const qc = useQueryClient();
  const historyQuery = useQuery({ queryKey: HISTORY_KEY, queryFn: getBankReconcileHistory });

  const handleFile = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    setUploading(true);
    setPreview(null);
    setSkipped(new Set());
    setFilter('ALL');
    try {
      const fileBase64 = await fileToBase64(file);
      const res = await previewBankReconcile({ fileName: file.name, fileBase64 });
      setPreview(res);
      if (res.summary.missing + res.summary.mismatch === 0) {
        toast.info('Nothing to reconcile - every invoice row already matches.');
      }
    } catch {
      toast.error('Could not read that file. Upload the Bank Book "Working Sheet" .xlsx.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const toggleSkip = (rowIndex: number): void => {
    setSkipped((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) next.delete(rowIndex);
      else next.add(rowIndex);
      return next;
    });
  };

  // Rows the commit will touch: missing (create) + mismatch (update), not skipped.
  const toApply = useMemo(
    () =>
      (preview?.rows ?? []).filter(
        (r) =>
          (r.status === 'MISSING' || r.status === 'MISMATCH') &&
          r.kind !== null &&
          !skipped.has(r.rowIndex),
      ),
    [preview, skipped],
  );

  const handleCommit = async (): Promise<void> => {
    if (!preview || toApply.length === 0) return;
    setCommitting(true);
    try {
      const rows: BankReconcileCommitRow[] = toApply.map((r) => ({
        kind: r.kind!,
        action: r.status === 'MISSING' ? 'create' : 'update',
        invoiceId: r.invoiceId!,
        supplierLineId: r.supplierLineId,
        existingId: r.existingId,
        amountPaise: r.amountPaise,
        date: r.date,
        bankName: r.bankName,
        bankAccountId: r.bankAccountId,
        reference: r.bankNarration,
        counterparty: r.counterparty,
        invoiceNumber: r.invoiceNumber,
      }));
      const result = await commitBankReconcile({
        rows,
        fileName: preview.fileName,
        sheet: preview.sheet,
      });
      if (result.failed > 0) {
        toast.warning(
          `Applied ${result.created} new + ${result.updated} updated, ${result.failed} failed.`,
        );
      } else {
        toast.success(`Applied ${result.created} new + ${result.updated} updated entries.`);
      }
      // The run is now logged - refresh the history list.
      void qc.invalidateQueries({ queryKey: HISTORY_KEY });
      // Re-run the preview so the table reflects what is now recorded.
      await rerun();
    } catch {
      toast.error('Could not apply the reconciliation.');
    } finally {
      setCommitting(false);
    }
  };

  // Re-preview is only possible with the original file; the input is cleared, so
  // after a commit we just drop the stale preview and prompt a re-upload.
  const rerun = async (): Promise<void> => {
    setPreview(null);
    setSkipped(new Set());
    toast.info('Re-upload the sheet to verify the applied changes.');
  };

  const rows = preview?.rows ?? [];
  const filteredRows = filter === 'ALL' ? rows : rows.filter((r) => r.status === filter);
  const summary = preview?.summary;

  return (
    <PageShell
      title="Bank Book Reconciliation"
      description='Upload the bank "Working Sheet" to verify receipts and supplier payments, then create the missing entries and fix the mismatched ones.'
      actions={
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={(e) => void handleFile(e.target.files?.[0])}
          />
          <UzButton
            leftIcon={<Upload className="h-4 w-4" />}
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
          >
            {uploading ? 'Reading...' : preview ? 'Upload another' : 'Upload bank book'}
          </UzButton>
        </div>
      }
    >
      {!preview && !uploading && (
        <UzCard>
          <div className="flex flex-col items-start gap-2 p-2">
            <p className="text-sm text-zinc-600">
              Upload the Bank Book <span className="font-medium">Working Sheet</span> (.xlsx). Each
              row with an invoice number is matched on invoice + amount:
            </p>
            <ul className="ml-4 list-disc text-sm text-zinc-600">
              <li>
                <span className="font-medium">Cr</span> (money in) against an invoice {'->'}{' '}
                customer receipt
              </li>
              <li>
                <span className="font-medium">Dr</span> (money out) on a Supplier Payment row {'->'}{' '}
                supplier payment
              </li>
              <li>
                Contra / expense / loan rows are reported as{' '}
                <span className="font-medium">N/A</span> and skipped
              </li>
            </ul>
          </div>
        </UzCard>
      )}

      {uploading && (
        <UzCard>
          <div className="flex items-center gap-3 p-4 text-sm text-zinc-600">
            <UzSpinner /> Reading and reconciling the sheet...
          </div>
        </UzCard>
      )}

      {preview && summary && (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <FilterChip
              label={`All (${summary.total})`}
              active={filter === 'ALL'}
              onClick={() => setFilter('ALL')}
            />
            <FilterChip
              label={`Matched (${summary.matched})`}
              active={filter === 'MATCHED'}
              onClick={() => setFilter('MATCHED')}
            />
            <FilterChip
              label={`Missing (${summary.missing})`}
              active={filter === 'MISSING'}
              onClick={() => setFilter('MISSING')}
            />
            <FilterChip
              label={`Mismatch (${summary.mismatch})`}
              active={filter === 'MISMATCH'}
              onClick={() => setFilter('MISMATCH')}
            />
            <FilterChip
              label={`Not found (${summary.notFound})`}
              active={filter === 'INVOICE_NOT_FOUND'}
              onClick={() => setFilter('INVOICE_NOT_FOUND')}
            />
            <FilterChip
              label={`Ambiguous (${summary.ambiguous})`}
              active={filter === 'INVOICE_AMBIGUOUS'}
              onClick={() => setFilter('INVOICE_AMBIGUOUS')}
            />
            <FilterChip
              label={`N/A (${summary.nonInvoice})`}
              active={filter === 'NON_INVOICE'}
              onClick={() => setFilter('NON_INVOICE')}
            />
          </div>

          <UzAlert
            tone={toApply.length > 0 ? 'info' : 'success'}
            className="mb-4"
            title={
              toApply.length > 0
                ? `${toApply.length} change${toApply.length === 1 ? '' : 's'} ready to apply`
                : 'Nothing to apply'
            }
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="text-sm">
                {preview.fileName} - sheet "{preview.sheet}". Missing rows create a new entry;
                mismatched rows update the recorded amount. Untick a row to skip it.
              </span>
              <UzButton
                disabled={committing || toApply.length === 0}
                onClick={() => void handleCommit()}
              >
                {committing ? 'Applying...' : `Apply ${toApply.length} change(s)`}
              </UzButton>
            </div>
          </UzAlert>

          <UzCard padding="none">
            {filteredRows.length === 0 ? (
              <p className="p-6 text-sm text-zinc-500">No rows in this category.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm whitespace-nowrap">
                  <thead>
                    <tr className="border-b border-zinc-200 text-left text-xs font-medium text-zinc-500">
                      <th className="px-3 py-3">Row</th>
                      <th className="px-3 py-3">Date</th>
                      <th className="px-3 py-3">Type</th>
                      <th className="px-3 py-3">Invoice / Heads</th>
                      <th className="px-3 py-3">Customer / Supplier</th>
                      <th className="px-3 py-3 text-right">Bank amount</th>
                      <th className="px-3 py-3 text-right">Recorded</th>
                      <th className="px-3 py-3">Status</th>
                      <th className="px-3 py-3">Apply</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row) => (
                      <ReconcileRowView
                        key={row.rowIndex}
                        row={row}
                        skipped={skipped.has(row.rowIndex)}
                        onToggleSkip={() => toggleSkip(row.rowIndex)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </UzCard>
        </>
      )}

      <UzCard className="mt-6" padding="none">
        <div className="flex items-center gap-2 border-b border-zinc-100 px-4 py-3">
          <History className="h-4 w-4 text-zinc-500" />
          <h2 className="text-sm font-semibold text-zinc-800">Reconciliation history</h2>
          {historyQuery.data && (
            <span className="text-xs text-zinc-500">({historyQuery.data.runs.length})</span>
          )}
        </div>
        {historyQuery.isLoading ? (
          <p className="p-6 text-sm text-zinc-500">Loading history...</p>
        ) : historyQuery.isError ? (
          <p className="p-6 text-sm text-red-600">Could not load the history.</p>
        ) : (historyQuery.data?.runs.length ?? 0) === 0 ? (
          <p className="p-6 text-sm text-zinc-500">
            No reconciliation runs yet. Applied changes will be logged here.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap">
              <thead>
                <tr className="border-b border-zinc-200 text-left text-xs font-medium text-zinc-500">
                  <th className="px-3 py-3">When</th>
                  <th className="px-3 py-3">File</th>
                  <th className="px-3 py-3">By</th>
                  <th className="px-3 py-3 text-right">Applied</th>
                  <th className="px-3 py-3 text-right">Created</th>
                  <th className="px-3 py-3 text-right">Updated</th>
                  <th className="px-3 py-3 text-right">Failed</th>
                  <th className="px-3 py-3" />
                </tr>
              </thead>
              <tbody>
                {(historyQuery.data?.runs ?? []).map((run) => (
                  <tr key={run.id} className="border-b border-zinc-100 last:border-0">
                    <td className="px-3 py-3 text-zinc-700 tabular-nums">
                      {formatRunTime(run.createdAt)}
                    </td>
                    <td className="px-3 py-3 text-zinc-700">
                      {run.fileName}
                      {run.sheet ? (
                        <span className="text-xs text-zinc-400"> ({run.sheet})</span>
                      ) : null}
                    </td>
                    <td className="px-3 py-3 text-zinc-700">{run.createdByName ?? '-'}</td>
                    <td className="px-3 py-3 text-right text-zinc-700 tabular-nums">
                      {run.totalRows}
                    </td>
                    <td className="px-3 py-3 text-right text-zinc-700 tabular-nums">
                      {run.created}
                    </td>
                    <td className="px-3 py-3 text-right text-zinc-700 tabular-nums">
                      {run.updated}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums">
                      {run.failed > 0 ? (
                        <span className="font-medium text-red-600">{run.failed}</span>
                      ) : (
                        <span className="text-zinc-400">0</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <UzButton size="sm" variant="ghost" onClick={() => setDetailRunId(run.id)}>
                        View
                      </UzButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </UzCard>

      <RunDetailDialog runId={detailRunId} onClose={() => setDetailRunId(null)} />
    </PageShell>
  );
};

/** Dialog showing one recorded run's applied rows and per-row result. */
const RunDetailDialog = ({
  runId,
  onClose,
}: {
  runId: string | null;
  onClose: () => void;
}): React.JSX.Element => {
  const detailQuery = useQuery({
    queryKey: ['reports', 'bank-reconcile', 'history', runId],
    queryFn: () => getBankReconcileRunDetail(runId as string),
    enabled: Boolean(runId),
  });
  const run = detailQuery.data;
  return (
    <UzDialog
      open={runId !== null}
      onOpenChange={(o) => !o && onClose()}
      title="Reconciliation run"
      description={
        run
          ? `${run.fileName}${run.sheet ? ` - sheet "${run.sheet}"` : ''} - ${formatRunTime(run.createdAt)}${run.createdByName ? ` by ${run.createdByName}` : ''}`
          : 'Loading...'
      }
      size="xl"
    >
      {detailQuery.isLoading ? (
        <p className="p-4 text-sm text-zinc-500">Loading...</p>
      ) : detailQuery.isError ? (
        <p className="p-4 text-sm text-red-600">Could not load the run.</p>
      ) : run ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="border-b border-zinc-200 text-left text-xs font-medium text-zinc-500">
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Action</th>
                <th className="px-3 py-2">Invoice</th>
                <th className="px-3 py-2">Customer / Supplier</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Result</th>
              </tr>
            </thead>
            <tbody>
              {run.rows.map((row, i) => (
                <tr key={i} className="border-b border-zinc-100 align-top last:border-0">
                  <td className="px-3 py-2 text-zinc-700">
                    {row.kind === 'RECEIPT' ? 'Receipt' : 'Supplier pay'}
                  </td>
                  <td className="px-3 py-2 text-zinc-700">
                    {row.action === 'create' ? 'Created' : 'Updated'}
                  </td>
                  <td className="px-3 py-2 text-zinc-700 tabular-nums">
                    {row.invoiceNumber ?? '-'}
                  </td>
                  <td className="px-3 py-2 text-zinc-700">{row.counterparty ?? '-'}</td>
                  <td className="px-3 py-2 text-right text-zinc-900 tabular-nums">
                    {formatInrPaise(row.amountPaise)}
                  </td>
                  <td className="px-3 py-2 text-zinc-700 tabular-nums">
                    {row.date ? formatDateDMY(row.date) : '-'}
                  </td>
                  <td className="px-3 py-2">
                    {row.result === 'ok' ? (
                      <UzBadge tone="success" pill>
                        OK
                      </UzBadge>
                    ) : (
                      <span className="flex flex-col gap-0.5">
                        <UzBadge tone="destructive" pill>
                          Failed
                        </UzBadge>
                        {row.error ? (
                          <span className="text-xs text-red-500">{row.error}</span>
                        ) : null}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {run.rows.length === 0 && (
            <p className="p-4 text-sm text-zinc-500">This run applied no rows.</p>
          )}
        </div>
      ) : null}
    </UzDialog>
  );
};

const ReconcileRowView = ({
  row,
  skipped,
  onToggleSkip,
}: {
  row: BankReconcileRow;
  skipped: boolean;
  onToggleSkip: () => void;
}): React.JSX.Element => {
  const meta = STATUS_META[row.status];
  const actionable = (row.status === 'MISSING' || row.status === 'MISMATCH') && row.kind !== null;
  return (
    <tr className="border-b border-zinc-100 align-top last:border-0">
      <td className="px-3 py-3 text-zinc-500 tabular-nums">{row.excelRow}</td>
      <td className="px-3 py-3 text-zinc-700 tabular-nums">
        {row.date ? formatDateDMY(row.date) : '-'}
      </td>
      <td className="px-3 py-3 text-zinc-700">
        {row.kind === 'RECEIPT' ? 'Receipt' : row.kind === 'SUPPLIER' ? 'Supplier pay' : '-'}
      </td>
      <td className="px-3 py-3 text-zinc-700">
        {row.invoiceId ? (
          <Link to={`/invoices/${row.invoiceId}`} className="text-primary hover:underline">
            {row.invoiceLabel ?? row.invoiceNumber}
          </Link>
        ) : (
          <span>{row.invoiceNumber ?? row.heads ?? '-'}</span>
        )}
        {row.note && <div className="text-xs text-zinc-400">{row.note}</div>}
      </td>
      <td className="px-3 py-3 text-zinc-700">{row.counterparty ?? '-'}</td>
      <td className="px-3 py-3 text-right font-medium text-zinc-900 tabular-nums">
        {formatInrPaise(row.amountPaise)}
      </td>
      <td className="px-3 py-3 text-right text-zinc-600 tabular-nums">
        {row.existingAmountPaise ? formatInrPaise(row.existingAmountPaise) : '-'}
      </td>
      <td className="px-3 py-3">
        <UzBadge tone={meta.tone} pill>
          {meta.label}
        </UzBadge>
      </td>
      <td className="px-3 py-3">
        {actionable ? (
          <UzCheckbox checked={!skipped} onCheckedChange={() => onToggleSkip()} />
        ) : (
          <span className="text-xs text-zinc-400">-</span>
        )}
      </td>
    </tr>
  );
};

const FilterChip = ({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): React.JSX.Element => (
  <button
    type="button"
    onClick={onClick}
    className={
      active
        ? 'border-primary bg-primary text-primary-foreground rounded-full border px-3 py-1 text-xs font-medium'
        : 'rounded-full border border-zinc-200 bg-white px-3 py-1 text-xs font-medium text-zinc-600 hover:bg-zinc-50'
    }
  >
    {label}
  </button>
);
